import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader } from './Card';
import { Button } from './Button';
import { Input } from './Input';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import type { EmployeeProfile } from '../store/profileStore';

type FieldType = 'text' | 'email' | 'date' | 'select' | 'textarea';
interface FieldDef { label: string; snake: keyof EmployeeProfile; camel: string; type?: FieldType; options?: string[] }

const BASIC: FieldDef[] = [
  { label: 'First name', snake: 'first_name', camel: 'firstName' },
  { label: 'Last name', snake: 'last_name', camel: 'lastName' },
  { label: 'Work phone', snake: 'phone', camel: 'phone' },
];

const PERSONAL: FieldDef[] = [
  { label: 'Personal email', snake: 'personal_email', camel: 'personalEmail', type: 'email' },
  { label: 'Personal phone', snake: 'personal_phone', camel: 'personalPhone' },
  { label: 'Date of birth', snake: 'date_of_birth', camel: 'dateOfBirth', type: 'date' },
  { label: 'Gender', snake: 'gender', camel: 'gender', type: 'select', options: ['', 'Male', 'Female', 'Other', 'Prefer not to say'] },
  { label: 'Marital status', snake: 'marital_status', camel: 'maritalStatus', type: 'select', options: ['', 'Single', 'Married', 'Other'] },
  { label: 'Blood group', snake: 'blood_group', camel: 'bloodGroup' },
  { label: 'Nationality', snake: 'nationality', camel: 'nationality' },
  { label: 'Current address', snake: 'current_address', camel: 'currentAddress', type: 'textarea' },
  { label: 'Permanent address', snake: 'permanent_address', camel: 'permanentAddress', type: 'textarea' },
  { label: 'Emergency contact name', snake: 'emergency_contact_name', camel: 'emergencyContactName' },
  { label: 'Emergency contact phone', snake: 'emergency_contact_phone', camel: 'emergencyContactPhone' },
  { label: 'Emergency contact relation', snake: 'emergency_contact_relation', camel: 'emergencyContactRelation' },
];

const dateOnly = (v?: string | null) => (v ? String(v).slice(0, 10) : '');

/** Employment facts an admin controls — always read-only in this form. */
function EmploymentFacts({ p }: { p: EmployeeProfile }) {
  const manager = [p.manager_first_name, p.manager_last_name].filter(Boolean).join(' ') || '—';
  const rows: [string, string][] = [
    ['Employee ID', p.employee_code || '—'],
    ['Work email', p.email || '—'],
    ['Department', p.department || '—'],
    ['Designation', p.designation || '—'],
    ['Reporting manager', manager],
    ['Employment type', p.employment_type || '—'],
    ['Status', p.status || '—'],
  ];
  return (
    <Card>
      <CardHeader className="px-5 py-3">
        <h3 className="font-display text-card-title text-foreground">Employment</h3>
        <p className="text-xs text-muted-foreground">Managed by HR / Admin — contact them to change these.</p>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map(([label, value]) => (
          <div key={label}>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-sm text-foreground">{value}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export function PersonalDetailsForm({
  profile, canEdit, includeBasic = false, onSave,
}: {
  profile: EmployeeProfile;
  canEdit: boolean;
  /** Also allow editing name / work phone (self-service only). */
  includeBasic?: boolean;
  onSave: (patch: Record<string, any>) => Promise<EmployeeProfile>;
}) {
  const fields = useMemo(() => (includeBasic ? [...BASIC, ...PERSONAL] : PERSONAL), [includeBasic]);
  const initial = useMemo(() => {
    const o: Record<string, string> = {};
    for (const f of fields) o[f.camel] = f.type === 'date' ? dateOnly(profile[f.snake] as any) : ((profile[f.snake] as any) ?? '');
    return o;
  }, [profile, fields]);

  const [draft, setDraft] = useState<Record<string, string>>(initial);
  const [saving, setSaving] = useState(false);

  const dirtyKeys = fields.filter((f) => (draft[f.camel] ?? '') !== (initial[f.camel] ?? '')).map((f) => f.camel);
  const dirty = dirtyKeys.length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const patch: Record<string, any> = {};
      for (const k of dirtyKeys) patch[k] = draft[k];
      const updated = await onSave(patch);
      // Re-baseline from server response.
      const next: Record<string, string> = {};
      for (const f of fields) next[f.camel] = f.type === 'date' ? dateOnly(updated[f.snake] as any) : ((updated[f.snake] as any) ?? '');
      setDraft(next);
      toast.success('Details saved');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const field = (f: FieldDef) => {
    const v = draft[f.camel] ?? '';
    if (!canEdit) return <p className="text-sm text-foreground">{v || '—'}</p>;
    const set = (val: string) => setDraft((d) => ({ ...d, [f.camel]: val }));
    if (f.type === 'textarea') {
      return <textarea value={v} onChange={(e) => set(e.target.value)} rows={2}
        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/20" />;
    }
    if (f.type === 'select') {
      return (
        <select value={v} onChange={(e) => set(e.target.value)}
          className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/20">
          {f.options!.map((o) => <option key={o} value={o}>{o || 'Not set'}</option>)}
        </select>
      );
    }
    return <Input type={f.type || 'text'} value={v} onChange={(e) => set(e.target.value)} />;
  };

  return (
    <div className="space-y-4">
      <EmploymentFacts p={profile} />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between px-5 py-3">
          <div>
            <h3 className="font-display text-card-title text-foreground">Personal details</h3>
            <p className="text-xs text-muted-foreground">{canEdit ? 'You can keep these up to date yourself.' : 'Read-only.'}</p>
          </div>
          {canEdit && (
            <Button variant="primary" size="sm" disabled={!dirty || saving} onClick={save}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : 'Save changes'}
            </Button>
          )}
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          {fields.map((f) => (
            <div key={f.camel} className={f.type === 'textarea' ? 'sm:col-span-2' : ''}>
              <p className="mb-1 text-xs font-medium text-muted-foreground">{f.label}</p>
              {field(f)}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
